/**
 * InquiryTable — the pipeline's working list.
 *
 * POL-16 redesign: the table no longer paints its own `card-glass`
 * box (the parent wrapper on Today already provides the ink-gradient
 * surface, so the old double box read as chrome-within-chrome).
 * Rows got the Pixieset-adjacent treatment: a tinted initial avatar
 * anchors each name, the header is a quiet uppercase eyebrow, and
 * separators are hairline so the list reads as one calm surface.
 */
import { Inquiry } from '@/types/crm';
import { StatusBadge } from './StatusBadge';
import { AlertTriangle, Eye, Inbox, User } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface InquiryTableProps {
  inquiries: Inquiry[];
  onViewDetails: (inquiry: Inquiry) => void;
}

// POL-16 — avatar tones cycle through the shared accent family so the
// list picks up the same multi-tone identity as the shortcut tiles.
// Deterministic per name so a lead keeps their color.
//
// BRAND-1 (Aug 2026) — was stock Tailwind teal/sky/red/emerald/amber/
// pink; now the 1960s space-race accents from index.css. Same array,
// same length, same hash consumer below — only the colour family
// moved, and shipping six of them is still deliberately NOT
// orange-for-everything. Order is arbitrary here on purpose: an
// avatar tone is an identity hash, not a status, so it must NOT be
// read as one of the role meanings (money, overdue, pending…).
//
// Each entry pairs the two token flavours, which are NOT
// interchangeable: `orbit-*-fill` is the flat chip background (same
// in both themes) and `orbit-*` is the readable text tone (theme-
// aware, which is why the old `dark:` variants are gone).
//
// Keep these fully spelled out — Tailwind's scanner reads the string
// literals, so never build one by concatenation.
const AVATAR_TONES = [
  'bg-orbit-teal-fill/[0.26] text-orbit-teal',
  'bg-orbit-blue-fill/[0.26] text-orbit-blue',
  'bg-orbit-red-fill/[0.26] text-orbit-red',
  'bg-orbit-phosphor-fill/[0.26] text-orbit-phosphor',
  'bg-orbit-gold-fill/[0.26] text-orbit-gold',
  'bg-orbit-rose-fill/[0.26] text-orbit-rose',
] as const;

function avatarTone(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length];
}

const headCls =
  'text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80';

/**
 * UX-5 — an accepted lead with no linked project is an INCOMPLETE
 * conversion: acceptance flips the status first, then a separate
 * write creates the project, and when that second write fails the
 * only evidence used to be a toast that faded. This predicate derives
 * the failure state from data (`status='accepted' AND project_id IS
 * NULL`), so the chip below is truthful across reloads with no
 * schema change and clears itself the moment a retry succeeds.
 *
 * Client-derived rows (`client-<uuid>`) are excluded: their status is
 * hardcoded 'accepted' by the dashboard merge — nothing failed there.
 *
 * NOTE this component is also mounted by the public marketing page
 * (FeatureSections.PipelineVisual) with static props, so it must stay
 * hook-free: the chip renders from data alone and the retry itself
 * lives in InquiryModal, which `onViewDetails` opens.
 */
export const isAcceptedWithoutProject = (inquiry: Inquiry): boolean =>
  inquiry.status === 'accepted' &&
  !inquiry.projectId &&
  !inquiry.id.startsWith('client-');

export const InquiryTable = ({ inquiries, onViewDetails }: InquiryTableProps) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (inquiries.length === 0) {
    return (
      <div className="p-8 text-center animate-fade-in">
        <div className="flex flex-col items-center gap-3">
          <div className="p-4 rounded-full bg-secondary dark:bg-white/[0.06]">
            <OrbitIcon name="tray" fallback={Inbox} className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground">No inquiries match your filters</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl animate-fade-in">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border dark:border-white/[0.06] hover:bg-transparent">
              <TableHead className={headCls}>Name</TableHead>
              <TableHead className={`${headCls} hidden md:table-cell`}>Email</TableHead>
              <TableHead className={`${headCls} hidden lg:table-cell`}>Interest</TableHead>
              <TableHead className={`${headCls} hidden lg:table-cell`}>Budget</TableHead>
              <TableHead className={headCls}>Status</TableHead>
              <TableHead className={`${headCls} hidden md:table-cell`}>Date</TableHead>
              <TableHead className={`${headCls} text-right`}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {inquiries.map((inquiry) => (
              <TableRow
                key={inquiry.id}
                className="border-border dark:border-white/[0.05] cursor-pointer transition-colors duration-150 hover:bg-secondary/60 dark:hover:bg-white/[0.04]"
                onClick={() => onViewDetails(inquiry)}
              >
                <TableCell className="font-medium text-foreground py-3.5">
                  <span className="flex items-center gap-3">
                    <span
                      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold ${avatarTone(inquiry.name)}`}
                      aria-hidden
                    >
                      {inquiry.name.trim().charAt(0).toUpperCase() || '?'}
                    </span>
                    <span className="flex items-center gap-2">
                      {inquiry.name}
                      {inquiry.id.startsWith('client-') && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-normal text-muted-foreground">
                          <OrbitIcon name="person.fill" fallback={User} className="w-3 h-3 mr-0.5" />Client
                        </Badge>
                      )}
                      {inquiry.formTag && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">
                          {inquiry.formTag}
                        </Badge>
                      )}
                    </span>
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">{inquiry.email}</TableCell>
                <TableCell className="text-muted-foreground hidden lg:table-cell">{inquiry.interest}</TableCell>
                <TableCell className="text-muted-foreground hidden lg:table-cell">{inquiry.budget}</TableCell>
                <TableCell>
                  <span className="flex flex-col items-start gap-1">
                    <StatusBadge status={inquiry.status} />
                    {/* UX-5 — persistent failure chip: survives the
                        toast because it derives from the row itself.
                        Clicking it opens the lead, where the retry
                        button re-runs the conversion. */}
                    {isAcceptedWithoutProject(inquiry) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewDetails(inquiry);
                        }}
                        title="This lead is accepted but its project was never created. Open it to retry."
                        className="inline-flex items-center gap-1 rounded-full border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-[10px] font-medium text-destructive hover:bg-destructive/20 transition-colors whitespace-nowrap"
                      >
                        <OrbitIcon name="exclamationmark.triangle.fill" fallback={AlertTriangle} className="w-3 h-3" />
                        Project not created — retry
                      </button>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {formatDate(inquiry.submittedAt)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDetails(inquiry);
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <OrbitIcon name="eye" fallback={Eye} className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
